import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Grid,
  Breadcrumbs,
  Typography,
  Button,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Table,
  Switch,
  IconButton,
  Stack
} from '@mui/material';
import { Add, Home } from '@mui/icons-material';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { EditIcon, StockIcon, TrashIcon } from '../../../common/Icons';
import {atualizaEstoque, carregaEstoques, carregaPontosEntrega, deletaEstoque} from './configs/functions';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { listaEmpresas, listaEmpresasHasStockModule } from '../../management/companies/configs/functions';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { StockForm } from './components/StockForm';
import { useAppContext } from '../../../../contexts/AppContext';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import { CustomToolbarExportTable } from '../../../common/CustomToolbarExportTable';
import {useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsNoCodeTableFilter} from "../../management/organization/components/ItemsNoCodeTableFilter";

const StockRow = ({ data, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData, empresaHasModule }) => {
  const [active, setActive] = useState(data.status[0]);
  const [openDialog, setOpenDialog] = useState(false);

  async function handleToggleStatus() {
    if (!hasPermission(["admin", "admin_estoques", "update_estoques"])) {
      handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
      return false;
    }

    try {
      const res = await atualizaEstoque(data.id, { status: !active })
      if (res) {
        const message = active ? 'Estoque inativado com sucesso' : 'Estoque ativado com sucesso'
        handleExibirAlerta('Sucesso', message, 'success')
        setActive(!active)
        handleUpdateStatus(data.id[0], !data.status[0])
      }
    } catch (error) {
      const messageError = error.response.data.error ?? 'Erro ao executar operação'
      handleExibirAlerta('Erro', messageError, 'error')
    }
  }

  const handleDelete = () => {
    const id = data.id[0];
    deletaEstoque(id)
      .then((response) => {
        const message = response.data.message ?? 'Estoque inativado com sucesso';
        handleExibirAlerta('Sucesso', message, 'success');
        handleUpdateStatus(id, false);
        setActive(false);
        updateData();
      }).catch((error) => {
        const message = error.response.data.message ?? 'Erro ao executar operação'
        handleExibirAlerta('Erro', message, 'error');
      }).finally(() => {
      if (updateData) updateData();
    })

    setOpenDialog(false)
  }

  return (
    <>
      <TableRow key={data.id}>
        <TableCell>{data.nome}</TableCell>
        <TableCell>{data.descricao}</TableCell>

        <TableCell>
          <Switch
            checked={!!active}
            onChange={handleToggleStatus}
            disabled={!hasPermission(["admin", "admin_estoques", "update_estoques"]) || !empresaHasModule}
          />
        </TableCell>

        {empresaHasModule &&
          <TableCell align='right' sx={{ minWidth: 110 }}>
            {hasPermission(["admin", "admin_estoques", "update_estoques"]) &&
              <IconButton
                aria-label="Editar"
                onClick={() => handleOpenEditForm(data)}
              >
                <EditIcon />
              </IconButton>}

            {hasPermission(["admin", "admin_estoques", "delete_estoques"]) &&
              <IconButton
                aria-label="Excluir"
                onClick={() => setOpenDialog(true)}
              >
                <TrashIcon />
              </IconButton>}
          </TableCell>
        }
      </TableRow>

      <ConfirmDialog
        description={'Tem certeza que deseja excluir esta estoque?'}
        title={'Excluir Estoque'}
        goAction={handleDelete}
        handleClose={setOpenDialog}
        state={openDialog}
      />
    </>
  )
}

export function ListOfStock() {
  const { exibirAlerta } = useCommonItems();
  const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();
  const { verifyIntegrationMode } = useModuleContext();

  // estados para controle de paginacao
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [numTotalItems, setNumTotalItems] = useState(0);

  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(false);
  const [openForm, setOpenForm] = useState(false)
  const [empresas, setEmpresas] = useState([]);
  const [empresasHasModule, setEmpresasHasModule] = useState([]);
  const [empresaHasModule, setEmpresaHasModule] = useState(false);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);

  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamPage = searchParams.get('page');
  const searchParamPerPage = searchParams.get('perPage');

  const columns = useMemo(() => [
    { field: 'nome', headerName: 'Nome' },
    { field: 'descricao', headerName: 'Descrição' },
    { field: 'classificacao', headerName: 'Classificação', valueGetter: (params) => params.row.classificacao[0] },
    { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status[0] === 1 ? 'Ativo' : 'Inativo' },
  ], []);

  const { data: empresasData, error: listaEmpresasError } = useQuery({
    queryKey: ['listaEmpresas'],
    queryFn: async () => {
      const response = await listaEmpresas(null, null, null, true);
      const empresasData = response.data.data;

      const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

      setEmpresaIdSession(selectedEmpresaId);

      setSearchParams((state) => {
        state.set('id_empresa', String(selectedEmpresaId));
        state.set('page', String(page + 1));
        state.set('perPage', String(rowsPerPage));

        return state;
      });
      return response;
    },
    refetchOnWindowFocus: false,
  });

  const queryFilters = {
    id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
    nome: searchParams.get('nome'),
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };

  const queryKeyListEstoques = ['listOfEstoques', queryFilters];
  const { data: estoquesData, isLoading, error: estoquesError, refetch } = useQuery({
    queryKey: queryKeyListEstoques,
    queryFn: () => carregaEstoques(queryFilters),
    enabled: !!searchParamPage && !!searchParamPerPage,
  });

  const updateEstoqueStatus = (categoryId, status) => setStocks(
      stocks.map((item) => {
        if (item.id === categoryId) {
          item.status = status;
        }
        return item;
      })
  );

  function handleEditEstoque(item) {
    setSelectedStock(item);
    setOpenForm(true);
  }

  useEffect(() => {
    if (listaEmpresasError) {
      exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
    }

    if (estoquesError) {
      exibirAlerta('Erro', 'Erro ao carregar estoques', 'error');
    }
  }, [exibirAlerta, listaEmpresasError, estoquesError])

  useEffect(() => {
    if (estoquesData) {
      setStocks(estoquesData.data.data);
      setNumTotalItems(estoquesData.data.numero_total);
      setSelectedEmpresaId(searchParams.get('id_empresa'));
      setEmpresas(empresasData.data.data)
    }
  }, [estoquesData]);

  useEffect(() => {
    if (openForm) return;
    setSelectedStock(null);

    const queryFilters = { ...Object.fromEntries(searchParams) }

    const tableFilters = { ...queryFilters };
    delete tableFilters.page;
    delete tableFilters.perPage;

    const activeFilters = Object.keys(tableFilters).length;

    setActiveFilters(activeFilters);
    setIsOpenFilters(activeFilters > 0);

  }, [openForm, searchParams]);

  useEffect(() => {
    const requiredPermissionsView = ["admin", "admin_estoques", "list_estoques"];
    checkPermissionsAndRedirect(requiredPermissionsView);
  }, []);


  return (
    <>
      <Helmet title="Estoques" defer={false} />

      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Breadcrumbs>
            <LinkNavegacao to="/"><Home fontSize="small" /></LinkNavegacao>
            <Typography variant='span'>Gerenciamento</Typography>
            <LinkNavegacao to="/gerenciamento/organizacao">Organização</LinkNavegacao>
            <Typography variant='span'>Gestão de Estoque</Typography>
          </Breadcrumbs>
          <PageTitle icon={<StockIcon fontSize='large' />} title='Estoque' description='Administre os Estoque das empresas.' />
        </Grid>

        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
            <FiltersButton
                isOpenFilters={isOpenFilters}
                setIsOpenFilters={setIsOpenFilters}
                activeFilters={activeFilters}
            />

            {hasPermission(["admin", "admin_estoques", "create_estoques"]) &&
              <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpenForm(true)}
                >
                  Cadastrar Estoque
                </Button>
              </Typography>
            }
          </Stack>
        </Grid>

        <ItemsNoCodeTableFilter
            isOpenFilters={isOpenFilters}
            isLoadingList={isLoading}
            empresas={empresas || []}
            item={"Estoque"}
        />

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <CustomToolbarExportTable
              rows={stocks}
              columns={columns}
              reportName="relatorio_estoques"
              reportPdfTitle="RELATÓRIO DE ESTOQUES"
              getDataWithoutPaginate={() => carregaEstoques({ id_empresas: [selectedEmpresaId] })}
              getSelectedEmpresa={() => empresas.find(empresa => empresa.id === Number(selectedEmpresaId))}
            />

            <Table aria-label="Estoques">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Status</TableCell>
                  {empresaHasModule && <TableCell sx={{ minWidth: { md: 120 } }}></TableCell>}
                </TableRow>
              </TableHead>

              <TableBody>
                {stocks?.length > 0
                  ? stocks.map((item) =>
                    <StockRow
                      key={item.id}
                      data={item}
                      handleExibirAlerta={exibirAlerta}
                      handleOpenEditForm={handleEditEstoque}
                      handleUpdateStatus={updateEstoqueStatus}
                      updateData={refetch}
                      empresaHasModule={empresaHasModule}
                    />
                  )
                  : isLoading ? <LoadingTableRow /> : <EmptyTableRow />
                }
              </TableBody>
            </Table>

            <CustomTablePagination
              numTotalItems={numTotalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          </TableContainer>
        </Grid>
      </Grid>

      <StockForm open={openForm} setOpen={setOpenForm} selectedStock={selectedStock} empresas={empresas} empresasHasModule={empresasHasModule} />
    </>
  );
}